import { useState, useEffect, useCallback } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { Mail, Plus, Trash2, Edit3, Loader2, Save, Eye, Copy, Code } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../App";
import Sidebar from "../../components/Sidebar";
import VisualEmailEditor from "../../components/marketing/VisualEmailEditor";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { name: "", subject: "", body_html: "", editor_config: null, pipeline_ids: [], all_pipelines: true };

export default function PlantillasPage() {
  const { getAuthHeaders, token, user } = useAuth();
  const isAdmin = ["admin", "administrador", "superadmin"].includes((user?.role || "").toLowerCase());
  const [plantillas, setPlantillas] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showVisual, setShowVisual] = useState(false);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rp, rpipe] = await Promise.all([
        fetch(`${API_URL}/api/pipelines/plantillas`, getAuthHeaders()),
        fetch(`${API_URL}/api/pipelines`, getAuthHeaders()),
      ]);
      setPlantillas((await rp.json()).items || []);
      const pipes = (await rpipe.json()).items || [];
      setPipelines(pipes);
      if (pipes[0]) {
        const rv = await fetch(`${API_URL}/api/pipelines/variables/${pipes[0].id}`, getAuthHeaders());
        setVariables((await rv.json()).items || []);
      }
    } catch { toast.error("Error al cargar plantillas"); }
    finally { setLoading(false); }
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing.name.trim()) { toast.error("La plantilla necesita un nombre"); return; }
    setSaving(true);
    try {
      const isEdit = !!editing.id;
      const r = await fetch(`${API_URL}/api/pipelines/plantillas${isEdit ? "/" + editing.id : ""}`, {
        method: isEdit ? "PUT" : "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify(editing),
      });
      if (!r.ok) throw new Error();
      toast.success("Plantilla guardada");
      setEditing(null); load();
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar la plantilla "${p.name}"?`)) return;
    try {
      await fetch(`${API_URL}/api/pipelines/plantillas/${p.id}`, { method: "DELETE", headers: getAuthHeaders().headers });
      toast.success("Plantilla eliminada"); load();
    } catch { toast.error("Error al eliminar"); }
  };

  const doPreview = async () => {
    try {
      const r = await fetch(`${API_URL}/api/pipelines/plantillas/preview`, {
        method: "POST", headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ subject: editing.subject, body_html: editing.body_html }),
      });
      setPreview(await r.json());
    } catch { toast.error("Error al generar vista previa"); }
  };

  const copyVar = (token) => { navigator.clipboard.writeText(token); toast.success(`${token} copiado`); };

  const pipeNames = (p) => p.all_pipelines ? "Todos" : (p.pipeline_ids || []).map((id) => pipelines.find((pp) => pp.id === id)?.name || "?").join(", ") || "Ninguno";

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 ml-0 md:ml-64 overflow-x-hidden">
        <div className="p-4 pt-14 md:pt-4 max-w-[1400px] mx-auto" data-testid="plantillas-page">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#193966] to-[#80BC00] text-white flex items-center justify-center">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-[#193966]">Plantillas de correo</h1>
                <p className="text-xs text-slate-500">Diseñá los correos del CRM y enlazalos a tus pipelines</p>
              </div>
            </div>
            {isAdmin && (
              <Button size="sm" onClick={() => setEditing({ ...EMPTY })} className="h-9 bg-[#80BC00] hover:bg-[#6ea700] text-white" data-testid="plantilla-new-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Nueva plantilla
              </Button>
            )}
          </div>

          {loading ? (
            <div className="text-center p-12"><Loader2 className="w-7 h-7 mx-auto animate-spin text-slate-400" /></div>
          ) : plantillas.length === 0 ? (
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-10 text-center bg-white">
              <Mail className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <h2 className="text-lg font-bold text-slate-700 mb-2">Aún no tenés plantillas</h2>
              <p className="text-sm text-slate-500 mb-4">Creá la primera plantilla para usarla en tus automatizaciones de Workflow.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {plantillas.map((p) => (
                <div key={p.id} className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm flex flex-col" data-testid={`plantilla-card-${p.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-bold text-slate-800 text-sm">{p.name}</h3>
                    <Badge variant="outline" className="text-[10px] shrink-0">{pipeNames(p)}</Badge>
                  </div>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-1">{p.subject || "Sin asunto"}</p>
                  <div className="text-[11px] text-slate-400 mt-2 border rounded p-2 bg-slate-50 line-clamp-3" dangerouslySetInnerHTML={{ __html: p.body_html || "<i>Sin contenido</i>" }} />
                  {isAdmin && (
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" variant="outline" className="h-8 flex-1" onClick={() => setEditing({ ...EMPTY, ...p })} data-testid={`plantilla-edit-${p.id}`}>
                        <Edit3 className="w-3.5 h-3.5 mr-1" /> Editar
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 text-rose-600" onClick={() => remove(p)} data-testid={`plantilla-delete-${p.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Editor dialog */}
      {editing && (
        <Dialog open onOpenChange={() => setEditing(null)}>
          <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="plantilla-dialog">
            <DialogHeader><DialogTitle>{editing.id ? "Editar plantilla" : "Nueva plantilla"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2 space-y-3">
                <div>
                  <Label className="text-xs">Nombre interno *</Label>
                  <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Ej: Bienvenida de lead" data-testid="plantilla-name" />
                </div>
                <div>
                  <Label className="text-xs">Asunto del correo</Label>
                  <Input value={editing.subject} onChange={(e) => setEditing({ ...editing, subject: e.target.value })} placeholder="Hola {{cliente}}, sobre tu seguro de {{ramo}}" data-testid="plantilla-subject" />
                </div>
                <div>
                  <Label className="text-xs">Contenido del correo</Label>
                  <div className="flex gap-2 mt-1">
                    <Button size="sm" variant="outline" className="h-8" onClick={() => setShowVisual(true)} data-testid="plantilla-visual-btn">
                      <Edit3 className="w-3.5 h-3.5 mr-1" /> Editor Visual
                    </Button>
                    <Button size="sm" variant="outline" className="h-8" onClick={doPreview} data-testid="plantilla-preview-btn">
                      <Eye className="w-3.5 h-3.5 mr-1" /> Vista previa
                    </Button>
                  </div>
                  <div className="mt-2">
                    <Label className="text-[10px] text-slate-400 flex items-center gap-1"><Code className="w-3 h-3" /> HTML (podés editar directo o usar el Editor Visual)</Label>
                    <Textarea value={editing.body_html} onChange={(e) => setEditing({ ...editing, body_html: e.target.value, editor_config: null })} rows={8} className="font-mono text-xs" data-testid="plantilla-body" />
                  </div>
                  {editing.body_html && (
                    <div className="mt-2 border rounded p-3 bg-white" dangerouslySetInnerHTML={{ __html: editing.body_html }} />
                  )}
                </div>
                <div>
                  <Label className="text-xs">Enlazar a pipelines</Label>
                  <label className="flex items-center gap-2 mt-1 text-sm">
                    <Checkbox checked={editing.all_pipelines} onCheckedChange={(v) => setEditing({ ...editing, all_pipelines: !!v })} data-testid="plantilla-all-pipelines" />
                    Disponible en todos los pipelines
                  </label>
                  {!editing.all_pipelines && (
                    <div className="mt-2 border rounded p-2 space-y-1 max-h-32 overflow-y-auto">
                      {pipelines.map((pp) => (
                        <label key={pp.id} className="flex items-center gap-2 text-sm">
                          <Checkbox
                            checked={(editing.pipeline_ids || []).includes(pp.id)}
                            onCheckedChange={(v) => {
                              const ids = new Set(editing.pipeline_ids || []);
                              if (v) ids.add(pp.id); else ids.delete(pp.id);
                              setEditing({ ...editing, pipeline_ids: [...ids] });
                            }}
                          />
                          {pp.name}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Variables panel */}
              <div className="col-span-1">
                <Label className="text-xs font-bold uppercase tracking-wide text-[#193966]">Variables disponibles</Label>
                <p className="text-[10px] text-slate-500 mb-2">Clic para copiar y pegar en el asunto o contenido.</p>
                <div className="border rounded p-2 bg-slate-50 max-h-[420px] overflow-y-auto space-y-1" data-testid="plantilla-variables">
                  {variables.map((v) => (
                    <button key={v.key} type="button" onClick={() => copyVar(v.token)}
                      className="w-full text-left px-2 py-1.5 rounded hover:bg-white border border-transparent hover:border-slate-200 transition-colors group">
                      <div className="flex items-center justify-between">
                        <code className="text-[11px] text-[#193966] font-semibold">{v.token}</code>
                        <Copy className="w-3 h-3 text-slate-300 group-hover:text-slate-500" />
                      </div>
                      <p className="text-[10px] text-slate-500">{v.label}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
              <Button onClick={save} disabled={saving} className="bg-[#80BC00] hover:bg-[#6ea700] text-white" data-testid="plantilla-save">
                {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} Guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Visual editor */}
      {showVisual && editing && (
        <Dialog open onOpenChange={setShowVisual}>
          <DialogContent className="max-w-6xl h-[90vh]">
            <VisualEmailEditor
              token={token}
              articulos={[]}
              initialConfig={editing.editor_config}
              onSave={(html, cfg) => { setEditing({ ...editing, body_html: html, editor_config: cfg }); setShowVisual(false); toast.success("Diseño guardado"); }}
              onCancel={() => setShowVisual(false)}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Preview */}
      {preview && (
        <Dialog open onOpenChange={() => setPreview(null)}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="plantilla-preview-dialog">
            <DialogHeader><DialogTitle>Vista previa (datos de ejemplo)</DialogTitle></DialogHeader>
            <p className="text-sm"><b>Asunto:</b> {preview.subject}</p>
            <div className="border rounded p-4 bg-white mt-2" dangerouslySetInnerHTML={{ __html: preview.body_html }} />
            <DialogFooter><Button variant="outline" onClick={() => setPreview(null)}>Cerrar</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
